import {
  FilterSelectedTags,
  LoadingListCard,
  UnitCardList,
} from "Explore/Components";
import {
  getSearchUnitGraph,
  searchUnits,
} from "Explore/Services/Unit";
import { Alert, Empty, Tag } from "antd";
import { useEffect, useRef, useState, useCallback } from "react";
import { FiltersUnitMobile } from "./Components";
import styles from "./UnitList.module.scss";
import useFilterAndPagination from "Hooks/useFilterAndPagination";
import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BubbleChartIcon from "@mui/icons-material/BubbleChart";
import { BookmarkPlus } from "lucide-react";
import SaveResultsModal from "Explore/Pages/MyLibrary/Components/SavedSearchResultsPanel/SaveResultsModal";
import { ParamsFilterKey } from "Config/filterSearchParams";
import { Helmet } from "react-helmet-async";
import useDeviceCheck from "Hooks/useDeviceCheck";
import FiltersUnit from "./Components/FiltersUnit";
import D3Graph from "Explore/Components/Graph";
import SuggestedKeywords from "Explore/Components/SuggestedKeywords/SuggestedKeywords";
import { getRelatedKeywords } from "Explore/Services/Common";
import { getExampleConceptExplore } from "Explore/Services/Concept";
import CallToLogin from "Explore/Components/CallToLogin";
import FeatureEntities from "Explore/Components/FeatureEntities";
import classNames from "classnames";
import KeywordClickSnackBar from "Explore/Components/KeywordClickSnackBar";
import SortEntity from "Explore/Components/SortEntity";
import { useParams } from "react-router-dom";
import useAuth from "Hooks/useAuth";
import LoginRequiredSection from "Explore/Components/LoginRequired";
import InfinityPagination from "Explore/Components/Pagination/InfinityPagination";
import useInfinityPagination from "Hooks/useInfinityPagination";

const UNIT_FILTER_DEFAULT = { [ParamsFilterKey.TOP_K]: 10 };

const UnitList = () => {
  const { userTypeConfig } = useAuth({ entityType: "UNIT" });
  const { isSmallDesktop, isTablet } = useDeviceCheck();
  const { filter, pagination, updateSearchParams, getPageHref } =
    useFilterAndPagination(UNIT_FILTER_DEFAULT);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [graphModalOpen, setGraphModalOpen] = useState(false);
  const [graphData, setGraphData] = useState(null);
  const [graphLoading, setGraphLoading] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);

  const handleFetchTopUnits = useCallback(
    async (targetLimit) => {
      const res = await searchUnits({
        ...filter,
        orderBy: "citation_count",
        order: "desc",
        page: 0,
        rowsPerPage: targetLimit,
        numPages: 1,
      });
      return Array.isArray(res) ? res : res?.results || [];
    },
    [filter]
  );
  const [showLoginSection, setShowLoginSection] = useState(false);
  const [keywordsUsedForSearch, setKeywordsUsedForSearch] = useState([]);
  const [suggestedKeywords, setSuggestedKeywords] = useState({
    data: [],
    isLoading: true,
  });
  const scrollToRef = useRef(null);
  const { keyword } = useParams();
  const titleKeyword =
    filter?.[ParamsFilterKey.KEYWORD_QUERY]?.[0] ||
    filter?.[ParamsFilterKey.QUERY]?.[0] ||
    keyword;

  const {
    data: listData,
    currentMaxPage,
    hasNextPage,
    isLoading,
  } = useInfinityPagination({
    fetchFunction: searchUnits,
    page: pagination.page,
    filter: {
      ...filter,
      orderBy: "citation_count",
      order: "desc",
    },
    rowsPerPage: pagination.rowsPerPage,
    setPagination: updateSearchParams,
    setKeywordsUsedForSearch,
    errCallback: (e) => {
      if (e?.response?.status === 401) {
        setShowLoginSection(true);
      }
    },
  });

  const pageToCompute = pagination.page - 1;

  const numPagesPerFetch = userTypeConfig?.limit?.numPagesPerFetch || 1;

  const showSort = !!filter[ParamsFilterKey.KEYWORD_QUERY]?.length;

  const showInformationNotesAboutKeywordUsedToSearch =
    keywordsUsedForSearch.length > 1;

  const informationNotesAboutKeywordUsedToSearchDisplayMessage = (
    <>
      <p>
        Sorry! We do not have <b>{filter.keywordQuery}</b> in our keyword
        glossary. Instead, we are using the following keywords to search
      </p>
      <div>
        {keywordsUsedForSearch.map((kw, index) => (
          <Tag key={index}>{kw.name}</Tag>
        ))}
      </div>
    </>
  );

  useEffect(() => {
    if (keyword) {
      updateSearchParams({
        [ParamsFilterKey.QUERY]: [keyword],
      });
    }
  }, [keyword]);

  useEffect(() => {
    scrollToRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [pagination.page]);

  // Reset the keyword note and reload suggested keywords when the filter changes.
  useEffect(() => {
    setKeywordsUsedForSearch([]);
    setSuggestedKeywords({ data: [], isLoading: true });

    if (filter[ParamsFilterKey.KEYWORD_QUERY]?.length > 0) {
      getRelatedKeywords({
        keywords: filter[ParamsFilterKey.KEYWORD_QUERY],
        entity: "units",
      }).then((res) => {
        setSuggestedKeywords({ data: res, isLoading: false });
      });
    } else {
      getExampleConceptExplore(0, 25, (res) => {
        setSuggestedKeywords({ data: res.results, isLoading: false });
      });
    }
  }, [JSON.stringify(filter)]);

  const handleOpenGraphModal = () => {
    setGraphModalOpen(true);
  };

  // Fetch graph data when the modal opens or the filter changes while open.
  useEffect(() => {
    if (graphModalOpen) {
      setGraphLoading(true);
      getSearchUnitGraph(filter).then((res) => {
        setGraphData(res.data);
        setGraphLoading(false);
      });
    }
  }, [JSON.stringify(filter), graphModalOpen]);

  const handleSuggestedKeywordClick = (keywords) => {
    const filterKeywords = [...(filter[ParamsFilterKey.KEYWORD_QUERY] || [])];
    keywords.forEach((keyword) => {
      if (!filterKeywords.includes(keyword)) {
        filterKeywords.push(keyword);
      }
    });

    updateSearchParams({
      [ParamsFilterKey.KEYWORD_QUERY]: filterKeywords,
    });
  };

  const onKeywordClick = (keyword) => {
    const _filter = filter?.[[ParamsFilterKey.KEYWORD_QUERY]] || [];
    if (_filter.includes(keyword)) {
      setSnackBarOpen(true);
      return;
    }

    updateSearchParams({
      [ParamsFilterKey.KEYWORD_QUERY]: [..._filter, keyword],
    });
  };

  const currentUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;

  const generateTitle = () => {
    // Advanced title logic (commented out for future version)

    if (!titleKeyword) return "Top Academic Units | Academic Online";
    const suffix = " | Academic Online";
    const maxLength = 65;

    let titleFull = `Top Academic Units in ${titleKeyword}`;
    if ((titleFull + suffix).length <= maxLength) return titleFull + suffix;

    let titleShort = `${titleKeyword} Academic Units`;
    if ((titleShort + suffix).length <= maxLength) return titleShort + suffix;

    return `${titleKeyword.substring(0, 40)}...${suffix}`;

    // Original simple logic
    // return "Explore Departments & Schools | Academic Explorer";
  };

  const generateDescription = () => {
    return titleKeyword
      ? `Find and explore academic units, departments, and research centers in ${titleKeyword}.`
      : "Search and discover academic units and departments across universities. Find information about programs, faculty, and research initiatives.";
  };

  return (
    <>
      <Helmet>
        <title>{generateTitle()}</title>
        <meta name="description" content={generateDescription()} />
        <link rel="canonical" href={currentUrl} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={generateTitle()} />
        <meta property="og:description" content={generateDescription()} />
      </Helmet>
      <div className={styles["unit-list"]}>
        {!isSmallDesktop && <FiltersUnit />}
        <div className={styles["results"]}>
          <div className={styles["results__header"]}>
            <h1 className={styles["results--title"]}>
              Explore Our Departments and Schools
            </h1>
            <p className={styles["results--description"]}>
              Explore the different{" "}
              <b>
                <em>schools and departments</em>
              </b>{" "}
              within our institution, each dedicated to specific fields of study
              and research.
            </p>
          </div>

          <div className={styles["results__body"]}>
            <SuggestedKeywords
              keywords={suggestedKeywords}
              onKeywordClick={handleSuggestedKeywordClick}
            />

            <div className={styles["results--controller"]}>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  startIcon={<BubbleChartIcon />}
                  onClick={handleOpenGraphModal}
                  className={styles["graph-button"]}
                  sx={{
                    borderRadius: "20px",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  View Network Graph
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<BookmarkPlus size={18} />}
                  onClick={() => setSaveModalOpen(true)}
                  className={styles["graph-button"]}
                  disabled={!Array.isArray(listData[pageToCompute]) || listData[pageToCompute].length === 0}
                  sx={{
                    borderRadius: "20px",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Save Search Results
                </Button>
              </div>

              <div className={styles["results--controller__right"]}>
                {isSmallDesktop && <FiltersUnitMobile />}
                {showSort && <SortEntity />}
              </div>
            </div>

            <FilterSelectedTags
              showLabel
              filters={filter}
              setFilters={updateSearchParams}
            />

            {/* information notes about using keywords */}
            {showInformationNotesAboutKeywordUsedToSearch && (
              <>
                <Alert
                  message={
                    informationNotesAboutKeywordUsedToSearchDisplayMessage
                  }
                  type="info"
                  showIcon
                />
                <br />
              </>
            )}

            <div ref={scrollToRef}>
              <LoadingListCard
                isLoading={isLoading}
                grid={{ gutter: 25, column: 1 }}
                componentNum={15}
                component={
                  Array.isArray(listData[pageToCompute]) &&
                  listData[pageToCompute].length > 0 ? (
                    <div className={styles["content-faculty"]}>
                      <Grid container spacing="25px">
                        {listData[pageToCompute].map((e, i) => (
                          <Grid item xs={12} key={i}>
                            <UnitCardList
                              data={e}
                              className={classNames(
                                styles["card"],
                                i % 2 === 0
                                  ? styles["even-card"]
                                  : styles["odd-card"],
                              )}
                              onKeywordClick={onKeywordClick}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </div>
                  ) : showLoginSection ? (
                    <LoginRequiredSection />
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Empty />
                    </div>
                  )
                }
              />

              <InfinityPagination
                pagination={pagination}
                setPagination={updateSearchParams}
                sizeChanger={userTypeConfig?.ui_components?.pageSizeChanger ?? true}
                pageSizeOptions={[15, 30, 45, 60]}
                currentMaxPage={currentMaxPage}
                hasNextPage={hasNextPage}
                isLoading={isLoading}
                addLink
                getPageHref={getPageHref}
                numPagesPerFetch={numPagesPerFetch}
              />
            </div>
          </div>
        </div>
        <div className={styles["right-section"]}>
          {!isTablet && <CallToLogin />}
          <FeatureEntities />
        </div>
      </div>

      <KeywordClickSnackBar open={snackBarOpen} setOpen={setSnackBarOpen} />

      {/* Graph Modal */}
      <Dialog
        open={graphModalOpen}
        onClose={() => setGraphModalOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: "85vh",
            maxHeight: "85vh",
            borderRadius: "20px",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            background: "#fff",
            color: "#0f3460",
            padding: "28px 24px",
            minHeight: "70px",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <BubbleChartIcon sx={{ fontSize: 28 }} />
            <span style={{ fontWeight: 700, fontSize: "1.25rem" }}>
              Unit Network Graph
            </span>
          </Box>
          <IconButton
            onClick={() => setGraphModalOpen(false)}
            size="small"
            sx={{
              position: "absolute",
              right: 16,
              color: "#0f3460",
              backgroundColor: "rgba(0,0,0,0.05)",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            padding: 0,
            overflow: "hidden",
            height: "100%",
            background: "#fff",
          }}
        >
          <D3Graph
            data={graphData || { nodes: [], links: [] }}
            isLoading={graphLoading}
            graphType="unit"
          />
        </DialogContent>
      </Dialog>
      <SaveResultsModal
        open={saveModalOpen}
        onOpenChange={setSaveModalOpen}
        entityType="units"
        currentItems={Array.isArray(listData[pageToCompute]) ? listData[pageToCompute] : []}
        currentFilters={filter}
        fetchItems={handleFetchTopUnits}
      />
    </>
  );
};

export default UnitList;
